import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";

import { ApiError, type FamilyApi } from "../lib/api";
import type { RuiGardenCommand, RuiGardenStatus } from "../lib/types";

interface RuiGardenPageProps {
  api: FamilyApi;
  offline: boolean;
}

export function RuiGardenPage({ api, offline }: RuiGardenPageProps) {
  const [status, setStatus] = useState<RuiGardenStatus | null>(null);
  const [brightness, setBrightness] = useState(50);
  const editingBrightness = useRef(false);
  const retryIntent = useRef<{ value: number; key: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const current = await api.getRuiGardenStatus();
      setStatus(current);
      if (!editingBrightness.current && !current.commands.some((command) => command.status === "pending")) {
        setBrightness((previous) => current.device?.brightnessPercent ?? previous);
      }
      setError(null);
    } catch (caught) {
      setError(messageFor(caught, "暫時讀不到開放板狀態。請稍後再試。"));
    }
  }, [api]);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => { if (navigator.onLine) void refresh(); }, 15_000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const submitBrightness = async (event: FormEvent) => {
    event.preventDefault();
    const intent = retryIntent.current?.value === brightness
      ? retryIntent.current
      : { value: brightness, key: crypto.randomUUID() };
    retryIntent.current = intent;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.setRuiGardenBrightness(brightness, intent.key);
      retryIntent.current = null;
      editingBrightness.current = false;
      setNotice("亮度設定已排隊，等開放板回報後才算套用。");
      await refresh();
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === "IDEMPOTENCY_CONFLICT") retryIntent.current = null;
      setError(messageFor(caught, "亮度設定沒有送出，請稍後再試。"));
    } finally {
      setBusy(false);
    }
  };

  const cancelCommand = async (commandId: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.cancelRuiGardenCommand(commandId);
      setNotice("待處理設定已取消。若開放板先一步套用，最新回報會更正紀錄。");
      await refresh();
    } catch (caught) {
      setError(messageFor(caught, "取消沒有完成，請重新整理後再試。"));
    } finally {
      setBusy(false);
    }
  };

  const device = status?.device;
  const lastSeen = device ? Date.parse(device.lastSeenAt) : NaN;
  const serverTime = status ? Date.parse(status.serverTime) : NaN;
  const connected = error === null && Number.isFinite(lastSeen) && Number.isFinite(serverTime) &&
    serverTime - lastSeen >= 0 && serverTime - lastSeen < 90_000;
  let connectionLabel = "正在確認開放板…";
  if (error) connectionLabel = "暫時無法確認開放板";
  else if (status !== null && connected) connectionLabel = "開放板已連線";
  else if (status !== null && device) connectionLabel = "開放板目前離線";
  else if (status !== null) connectionLabel = "開放板尚未連線";
  let connectionDetail = status === null ? "請稍候" : "等待第一次回報";
  if (device) connectionDetail = `最後回報 ${formatTime(device.lastSeenAt)}`;
  let deviceConnection = "尚未回報";
  if (error) deviceConnection = "待確認 · 顯示最後資料";
  else if (connected) deviceConnection = "已連線";
  else if (device) deviceConnection = "離線 · 顯示最後資料";

  return (
    <section className="rg-page" aria-labelledby="rg-title">
      <header className="rg-hero">
        <div className="rg-hero-copy">
          <p className="eyebrow">RUI GARDEN · 家長控制</p>
          <h1 id="rg-title">花園，慢慢長大。</h1>
          <p>看看開放板的近況，調整舒服的螢幕亮度。任務、點數與花朵，仍以板上保存的結果為準。</p>
          <div className="rg-connection" role="status">
            <span className={`rg-connection-dot${connected ? " is-online" : ""}`} aria-hidden="true" />
            <strong>{connectionLabel}</strong>
            <span>{connectionDetail}</span>
          </div>
        </div>
        <div className="rg-illustration" aria-hidden="true">
          <div className="rg-sun" /><div className="rg-hill rg-hill-back" /><div className="rg-hill rg-hill-front" />
          <div className="rg-rabbit"><i /><i /><b><span /><span /></b></div>
          <span className="rg-flower rg-flower-a">✿</span><span className="rg-flower rg-flower-b">✿</span>
        </div>
      </header>

      {error ? <p className="notice notice-error" role="alert">{error}<button type="button" onClick={() => void refresh()}>重新整理</button></p> : null}
      {notice ? <p className="notice notice-success" role="status">{notice}</p> : null}
      {status?.lastReset ? <p className="stale-note" role="status">板上資料曾回到較早的修訂。若做過還原或切換示範資料，請確認花園內容。</p> : null}

      <div className="rg-section-heading"><div><p className="eyebrow">GARDEN MIRROR</p><h2>現在的花園</h2></div><span>{device ? `修訂 ${device.gardenRevision}${device.firmware.includes("-demo") ? " · 示範資料" : ""}` : "等待板上資料"}</span></div>
      <div className="rg-overview" aria-label="花園狀態">
        <div className="rg-balance"><span className="rg-metric-label">目前點數</span><strong>{device?.balance ?? "—"}</strong><small>僅顯示板上已保存的餘額</small></div>
        <dl className="rg-garden-details">
          <div><dt><span aria-hidden="true">✿</span> 已種下的小花</dt><dd>{device?.flowersPlaced ?? "—"}</dd></div>
          <div><dt><span aria-hidden="true">▧</span> 背包裡的小花</dt><dd>{device?.flowersInBag ?? "—"}</dd></div>
          <div><dt><span aria-hidden="true">✓</span> 第一件任務</dt><dd>{taskStatus(device ?? null)}</dd></div>
        </dl>
      </div>

      <div className="rg-workspace">
        <section className="rg-device" aria-labelledby="rg-device-title">
          <p className="eyebrow">DEVICE</p><h2 id="rg-device-title">開放板</h2>
          <dl>
            <div><dt>連線</dt><dd>{deviceConnection}</dd></div>
            <div><dt>最後回報</dt><dd>{device ? formatTime(device.lastSeenAt) : "—"}</dd></div>
            <div><dt>目前亮度</dt><dd>{device ? `${device.brightnessPercent}%` : "—"}</dd></div>
            <div><dt>韌體</dt><dd>{device?.firmware ?? "—"}</dd></div>
          </dl>
          <p className="rg-footnote">離線時顯示最後資料，不代表現在的板上狀態。</p>
        </section>
        <section className="rg-control" aria-labelledby="rg-control-title">
          <p className="eyebrow">SCREEN CONTROL</p><h2 id="rg-control-title">亮一點，或柔和一點。</h2>
          <p>送出後先排隊。只有開放板確認套用，紀錄才會顯示「已套用」。</p>
          <form onSubmit={(event) => void submitBrightness(event)}>
            <label htmlFor="rg-brightness">螢幕亮度 <strong>{brightness}%</strong></label>
            <input id="rg-brightness" type="range" min="10" max="100" step="5" value={brightness} disabled={offline || busy} onChange={(event) => { setBrightness(Number(event.currentTarget.value)); editingBrightness.current = true; retryIntent.current = null; }} />
            <div className="rg-range"><span>柔和 10%</span><span>明亮 100%</span></div>
            <button className="button button-primary" type="submit" disabled={offline || busy}>{busy ? "處理中…" : "送出亮度設定"}</button>
          </form>
          <p className="rg-footnote">板子離線也能排隊；超過 24 小時仍未套用會逾期。</p>
        </section>
      </div>

      <section className="rg-activity" aria-labelledby="rg-activity-title">
        <div className="rg-section-heading"><div><p className="eyebrow">RECENT ACTIVITY</p><h2 id="rg-activity-title">設定紀錄</h2></div><span>最近 20 筆</span></div>
        {status?.commands.length ? <ul>{status.commands.map((command) => <CommandRow key={command.id} command={command} busy={offline || busy} onCancel={cancelCommand} />)}</ul> : <p className="rg-empty">還沒有設定紀錄。調整亮度後，會在這裡看到板子的確認進度。</p>}
      </section>
    </section>
  );
}

function CommandRow({ command, busy, onCancel }: {
  command: RuiGardenCommand;
  busy: boolean;
  onCancel: (commandId: string) => Promise<void>;
}) {
  return <li className="rg-command">
    <span className="rg-command-mark" aria-hidden="true">☀</span>
    <div><strong>亮度設為 {command.value}%</strong><time dateTime={command.createdAt}>{formatTime(command.createdAt)}</time></div>
    <span className={`rg-command-state rg-command-${command.status}`}>{commandLabel(command)}</span>
    {command.status === "pending" ? <button className="button button-ghost" type="button" disabled={busy} onClick={() => void onCancel(command.id)} aria-label={`取消亮度 ${command.value}% 的待處理設定`}>取消</button> : null}
  </li>;
}

function commandLabel(command: RuiGardenCommand): string {
  if (command.detail === "applied_after_cancelled_by_parent") return "取消後仍已套用";
  if (command.detail === "applied_after_expired") return "逾期後仍已套用";
  if (command.detail === "cancelled_by_parent") return "已取消";
  if (command.detail === "expired") return "已逾期";
  return { pending: "待板子確認", applied: "已套用", rejected: "未套用" }[command.status];
}

function formatTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : new Intl.DateTimeFormat("zh-TW", { dateStyle: "short", timeStyle: "short", timeZone: "Asia/Taipei" }).format(date);
}

function taskStatus(device: RuiGardenStatus["device"]): string {
  if (!device) return "—";
  return device.taskDone ? "已完成" : "未完成";
}

function messageFor(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.code === "IDEMPOTENCY_CONFLICT") {
    return "這次設定和先前請求不一致。請重新整理後再試。";
  }
  if (error instanceof ApiError && error.code === "CONSOLE_UNAVAILABLE") {
    return "花園控制台暫時連不上。請檢查網路，稍後再試。";
  }
  return error instanceof ApiError ? error.message : fallback;
}
